import { useState } from "react";
import { AlertCircle, CheckCircle, Download, FileText, Loader2, X, XCircle } from "lucide-react";
import { Button, Input } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { TellescopeModelFromName, useTellescopeMutation } from "@tellescope/tanstack-query";
import medicationTemplateCSV from "./medication_template.csv?url";
import { validateMedicationCSV, transformMedicationData } from "./utils";
import { toast } from "sonner";

export interface MedicationUploadFormProps {
  contact: TellescopeModelFromName<"endusers">;
  onUpload?: () => void;
}

type MedicationValidationResult = ReturnType<typeof validateMedicationCSV>;

interface UploadedFile {
  file: File;
  id: string;
  validationResult?: MedicationValidationResult;
  isValidating?: boolean;
}

export const MedicationUploadForm = ({ contact, onUpload }: MedicationUploadFormProps) => {
  const { t } = useTranslation("medications");
  const [selectedFiles, setSelectedFiles] = useState<UploadedFile[]>([]);

  const uploadMedicationCSVMutation = useTellescopeMutation({
    model: "enduser_medications",
    method: "createSome",

    onSuccess: () => {
      onUpload?.();
    },
    onError: () => {
      toast.error(t("Failed to upload medications. Please try again."));
    },
  });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter(
      (file) => file.type === "text/csv" || file.name.endsWith(".csv")
    );

    const newFiles: UploadedFile[] = files.map((file) => ({
      file,
      id: Math.random().toString(36),
      isValidating: true,
    }));

    setSelectedFiles((prev) => [...prev, ...newFiles]);

    // Validate each file asynchronously
    await Promise.all(
      newFiles.map(async (uploadedFile) => {
        try {
          const content = await uploadedFile.file.text();
          const validationResult = validateMedicationCSV(content);

          setSelectedFiles((prev) =>
            prev.map((f) => (f.id === uploadedFile.id ? { ...f, validationResult, isValidating: false } : f))
          );
        } catch (error) {
          setSelectedFiles((prev) =>
            prev.map((f) =>
              f.id === uploadedFile.id
                ? {
                    ...f,
                    validationResult: {
                      data: [],
                      errors: [
                        {
                          row: 0,
                          field: "",
                          message: `Failed to read file: ${error instanceof Error ? error.message : "Unknown error"}`,
                          severity: "error" as const,
                        },
                      ],
                      warnings: [],
                      isValid: false,
                      metadata: {
                        totalRows: 0,
                        validRows: 0,
                        invalidRows: 0,
                        headers: [],
                      },
                    },
                    isValidating: false,
                  }
                : f
            )
          );
        }
      })
    );
  };

  const removeFile = (id: string) => {
    setSelectedFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleSubmit = async () => {
    if (!selectedFiles.length) return;

    // Check if all files are validated
    const allValidated = selectedFiles.every((f) => !f.isValidating && f.validationResult);
    if (!allValidated) {
      return;
    }

    // Check if any files have critical errors
    const hasErrors = selectedFiles.some((f) => !f.validationResult?.isValid);
    if (hasErrors) {
      return;
    }

    // Collect all valid medication data from all files
    const allMedicationData = selectedFiles.flatMap((file) => {
      if (!file.validationResult?.data) return [];
      return transformMedicationData(file.validationResult.data, contact.id);
    });

    if (allMedicationData.length === 0) {
      return;
    }

    // Call the mutation with all medications
    await uploadMedicationCSVMutation.mutateAsync(allMedicationData);

    // Clear files after successful upload
    setSelectedFiles([]);
  };

  const downloadExample = () => {
    // Download the template CSV file
    const a = document.createElement("a");
    a.href = medicationTemplateCSV;
    a.download = "medication_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // TODO: add i18n
  return (
    <div className="flex-1 overflow-y-auto space-y-6 pr-2">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">{t("Upload Medication CSV Files")}</h3>
          <Button variant="outline" size="sm" onClick={downloadExample} className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            {t("Download Example")}
          </Button>
        </div>
      </div>

      {/* File Upload */}
      <div className="space-y-2 cursor-pointer">
        <Input
          id="medication-upload"
          type="file"
          multiple
          accept=".csv"
          onChange={handleFileSelect}
          placeholder={t("Choose CSV files")}
          className="cursor-pointer"
        />
      </div>

      {/* Uploaded Files List */}
      {selectedFiles.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-medium">{t("Uploaded Files")}</h4>
          <div className="space-y-3">
            {selectedFiles.map((uploadedFile) => {
              const validationResult = uploadedFile.validationResult;
              const errors = validationResult?.errors || [];
              const warnings = validationResult?.warnings || [];
              const validRowCount = validationResult?.data.length || 0;

              return (
                <div key={uploadedFile.id} className="border rounded-lg">
                  <div className="flex items-start gap-3 p-3">
                    <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{uploadedFile.file.name}</p>
                        {uploadedFile.isValidating && (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        )}
                        {!uploadedFile.isValidating && validationResult && (
                          <>
                            {validationResult.isValid && warnings.length === 0 && (
                              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                            )}
                            {validationResult.isValid && warnings.length > 0 && (
                              <AlertCircle className="h-4 w-4 text-yellow-600 flex-shrink-0" />
                            )}
                            {!validationResult.isValid && <XCircle className="h-4 w-4 text-red-600 flex-shrink-0" />}
                          </>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {(uploadedFile.file.size / 1024 / 1024).toFixed(2)} MB
                        {validationResult &&
                          !uploadedFile.isValidating &&
                          t(" • {{count}} valid row", { count: validRowCount })}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(uploadedFile.id)}
                      className="h-8 w-8 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Error and Warning Messages */}
                  {!uploadedFile.isValidating && validationResult && (errors.length > 0 || warnings.length > 0) && (
                    <div className="border-t px-3 py-2 space-y-2 bg-muted/30">
                      {errors.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs font-semibold text-red-700 flex items-center gap-1">
                            <XCircle className="h-3 w-3" />
                            {t("Errors {{amount}}", { amount: errors.length })}
                          </p>
                          {errors.slice(0, 3).map((error, idx) => (
                            <p key={idx} className="text-xs text-red-600 ml-4">
                              {error.row > 0 && `Row ${error.row}: `}
                              {error.message}
                            </p>
                          ))}
                          {errors.length > 3 && (
                            <p className="text-xs text-red-600 ml-4 italic">
                              {t("... and {{count}} more errors", { count: errors.length - 3 })}
                            </p>
                          )}
                        </div>
                      )}
                      {warnings.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs font-semibold text-yellow-700 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {t("Warnings ({{amount}})", { amount: warnings.length })}
                          </p>
                          {warnings.slice(0, 3).map((warning, idx) => (
                            <p key={idx} className="text-xs text-yellow-600 ml-4">
                              {warning.row > 0 && `Row ${warning.row}: `}
                              {warning.message}
                            </p>
                          ))}
                          {warnings.length > 3 && (
                            <p className="text-xs text-yellow-600 ml-4 italic">
                              {t("... and {{count}} more warnings", { count: warnings.length - 3 })}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Submit Button */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button variant="outline" onClick={() => setSelectedFiles([])} disabled={selectedFiles.length === 0}>
          Clear All
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={
            selectedFiles.length === 0 ||
            selectedFiles.some((f) => f.isValidating || !f.validationResult?.isValid) ||
            uploadMedicationCSVMutation.isPending
          }
        >
          {uploadMedicationCSVMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Importing...
            </>
          ) : (
            "Import Medications"
          )}
        </Button>
      </div>
    </div>
  );
};
